export const VPN_OTP_REQUIRED = "VPN_OTP_REQUIRED";

export type VpnOtpRequest = { vpnProfileId: string; vpnProfileName?: string };

/** Reads {vpnProfileId, vpnProfileName} from a VPN_OTP_REQUIRED error body, or null for other errors. */
export function vpnOtpRequestFromError(status: number | undefined, data: unknown): VpnOtpRequest | null {
  if (status !== 428 || !data || typeof data !== "object") return null;
  const response = (data as { response?: Record<string, unknown> }).response;
  if (!response || response.businessErrorCode !== VPN_OTP_REQUIRED) return null;
  const id = typeof response.vpnProfileId === "string" ? response.vpnProfileId : "";
  if (!id) return null;
  return {
    vpnProfileId: id,
    vpnProfileName: typeof response.vpnProfileName === "string" ? response.vpnProfileName : undefined,
  };
}

/**
 * Bridge between code that needs a VPN one-time password (axios interceptor, chat) and the dialog mounted once in
 * the app. Concurrent requests for the same profile share one prompt.
 */
type Listener = (request: VpnOtpRequest, resolve: (connected: boolean) => void) => void;

let listener: Listener | null = null;
const pending = new Map<string, Promise<boolean>>();

export function setVpnOtpPromptListener(next: Listener | null): void {
  listener = next;
}

/** Resolves true once the user connected the VPN, false if they cancelled (or no dialog is mounted). */
export function requestVpnOtp(request: VpnOtpRequest): Promise<boolean> {
  const existing = pending.get(request.vpnProfileId);
  if (existing) return existing;
  if (!listener) return Promise.resolve(false);
  const active = listener;
  const prompt = new Promise<boolean>((resolve) => active(request, resolve)).finally(() =>
    pending.delete(request.vpnProfileId)
  );
  pending.set(request.vpnProfileId, prompt);
  return prompt;
}
